import { useId, type InputHTMLAttributes, type ReactNode } from "react";
import "./Radio.css";

interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "children"> {
  /** Rendered beside the circle. */
  label: ReactNode;
  /** Optional second line explaining what choosing this will do. */
  hint?: ReactNode;
}

/**
 * The project's radio button — the missing sibling of {@link Checkbox}, built the same way.
 *
 * A real `<input type="radio">` is kept in the DOM and only visually replaced: it stays focusable,
 * keyboard-operable (including the browser's own arrow-key navigation within a group, which a
 * styled `<div>` would have to reimplement), announced correctly, and reachable by `form` and label
 * semantics.
 *
 * Prefer this over a native radio anywhere in the app. See "Design system" in web/CLAUDE.md.
 */
export const Radio = ({
  label,
  hint,
  className = "",
  disabled,
  ...props
}: RadioProps) => {
  const hintId = useId();

  // The hint is a SIBLING of the label, not inside it. A <label>'s text content becomes the
  // control's accessible NAME, so a hint nested inside it made the name "All events Every
  // occurrence, including ones already past." — the label and its own description read out as one
  // string. Outside the label it is description only, via aria-describedby.
  //
  // aria-describedby is merged rather than assigned, so a caller passing their own does not
  // silently drop the hint.
  const describedBy = [props["aria-describedby"], hint ? hintId : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={`radio-option ${className}`.trim()}>
      <label className={`radio${disabled ? " radio--disabled" : ""}`}>
        <span className="radio__control">
          <input
            type="radio"
            className="radio__input"
            disabled={disabled}
            {...props}
            aria-describedby={describedBy || undefined}
          />
          {/*
            aria-hidden: the input already carries the state. Announcing the dot as well would have
            a screen reader read the control twice.
          */}
          <span className="radio__circle" aria-hidden="true">
            <span className="radio__dot" />
          </span>
        </span>
        <span className="radio__label-text">{label}</span>
      </label>
      {hint && (
        <span className="radio__hint" id={hintId}>
          {hint}
        </span>
      )}
    </div>
  );
};

interface RadioGroupProps {
  /** Names the whole group. Rendered as a `<span>`, not a `<label>` — it labels a set, not an input. */
  label: ReactNode;
  children: ReactNode;
  className?: string;
  testId?: string;
}

/**
 * Wraps a set of {@link Radio}s with `role="radiogroup"` and a group label.
 *
 * The label is a `<span>` plus `aria-labelledby` rather than a `<label>`: a `<label>` naming a set
 * of controls rather than one input is wrong for screen readers and trips
 * `label-has-associated-control`. See "Accessibility" in web/CLAUDE.md.
 */
export const RadioGroup = ({
  label,
  children,
  className = "",
  testId,
}: RadioGroupProps) => {
  const labelId = useId();

  return (
    <div
      className={`radio-group ${className}`.trim()}
      role="radiogroup"
      aria-labelledby={labelId}
      data-testid={testId}
    >
      <span className="radio-group__label" id={labelId}>
        {label}
      </span>
      {children}
    </div>
  );
};
